import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./AutoApprovalRate.module.css";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { CODE_COUNTRY_FULL_NAME, ALL_COUNTRIES_WITH_JDWIN } from "../../../constants/constant";
import { 
  fetchAdminAutoApprovalRate, 
  fetchAvailableYearsForApprovalRate
} from "../../../store/slices/dashboard";
import {
  fetchCcmAutoApprovalRate,
  fetchAvailableYearsForCcmApprovalRate
} from "../../../store/slices/dashboard/ccmDashboardSlice";

const AutoApprovalRate = ({ initialData, isAdmin = false }) => {
  const dispatch = useDispatch();
  const { 
    autoApprovalRate: adminAutoApprovalRate, 
    autoApprovalRateLoading: adminAutoApprovalRateLoading,
    availableYears: adminAvailableYears,
    availableYearsLoading: adminAvailableYearsLoading,
    yearsWithMonths: adminYearsWithMonths
  } = useSelector((state) => state.adminDashboard);
  
  const {
    autoApprovalRate: ccmAutoApprovalRate,
    autoApprovalRateLoading: ccmAutoApprovalRateLoading,
    availableYears: ccmAvailableYears,
    yearsWithMonths: ccmYearsWithMonths
  } = useSelector((state) => state.ccmDashboard);
  
  const autoApprovalRate = isAdmin ? adminAutoApprovalRate : ccmAutoApprovalRate;
  const autoApprovalRateLoading = isAdmin ? adminAutoApprovalRateLoading : ccmAutoApprovalRateLoading;
  const availableYears = isAdmin ? adminAvailableYears : ccmAvailableYears;
  const availableYearsLoading = isAdmin ? adminAvailableYearsLoading : false;
  const yearsWithMonths = isAdmin ? adminYearsWithMonths : ccmYearsWithMonths;
  
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedFiscalMonth, setSelectedFiscalMonth] = useState("");
  const [availableMonthsForYear, setAvailableMonthsForYear] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [countries, setCountries] = useState([]);
  const [autoPercentage, setAutoPercentage] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [autoApproved, setAutoApproved] = useState(0);
  const [manualApproved, setManualApproved] = useState(0);
  const COLORS = {
    auto: '#367c2b', 
    manual: '#e0e0e0'
  };
  
  // Fetch available years on mount
  useEffect(() => {
    if (isAdmin) {
      // Add "ALL" option to countries
      const allCountries = ['ALL', ...ALL_COUNTRIES_WITH_JDWIN];
      setCountries(allCountries);
      setSelectedCountry(allCountries[0]);
      
      // Fetch available years only if not already loaded
      if (!availableYears || availableYears.length === 0) {
        dispatch(fetchAvailableYearsForApprovalRate());
      }
    } else {
      // For CCM users, fetch their available years
      dispatch(fetchAvailableYearsForCcmApprovalRate());
    }
  }, [isAdmin, dispatch]);
  
  // Set default year to most recent available year
  useEffect(() => {
    if (availableYears && availableYears.length > 0) {
      if (!selectedYear || !availableYears.includes(selectedYear)) {
        setSelectedYear(availableYears[0]); // Default to most recent year
      }
    }
  }, [availableYears]);
  
  // Update available months when year changes (from yearsWithMonths map)
  useEffect(() => {
    if (selectedYear && yearsWithMonths[selectedYear]) {
      setAvailableMonthsForYear(yearsWithMonths[selectedYear]);
    }
  }, [selectedYear, yearsWithMonths]);
  
  // Set default fiscal month to previous month (second month, not first)
  // Or preserve the same fiscal month if user changes year
  useEffect(() => {
    if (availableMonthsForYear && availableMonthsForYear.length > 0) {
      // If we have a selected month, try to find the same month name in the new year
      if (selectedFiscalMonth) {
        // Month is now just calendar name like "Jan", "Feb" (no year suffix)
        const matchingMonth = availableMonthsForYear.find(m => m === selectedFiscalMonth);
        
        if (matchingMonth) {
          // Month is already set correctly, no need to update
        } else {
          // If that month doesn't exist in the new year, default to first available
          setSelectedFiscalMonth(availableMonthsForYear[0]);
        }
      } else {
        // First time load - default to the first available month (most recent)
        // If it has no data, user will see "No data available" message
        const defaultMonth = availableMonthsForYear[0];
        setSelectedFiscalMonth(defaultMonth);
      }
    }
  }, [availableMonthsForYear]);
  
  // Fetch approval rate data when country, year, or fiscal month changes
  useEffect(() => {
    if (selectedCountry && selectedYear && selectedFiscalMonth) {
      if (isAdmin) {
        dispatch(fetchAdminAutoApprovalRate({ country: selectedCountry, year: selectedYear, month: selectedFiscalMonth }));
      } else {
        dispatch(fetchCcmAutoApprovalRate({ country: selectedCountry, year: selectedYear, month: selectedFiscalMonth }));
      }
    }
  }, [isAdmin, selectedCountry, selectedYear, selectedFiscalMonth, dispatch]);

  useEffect(() => {
    if (autoApprovalRate) {
      updateChartDataFromAPI(autoApprovalRate);
    }
  }, [autoApprovalRate]);

  const updateChartDataFromAPI = (data) => {
    if (!isAdmin && data.countries && data.countries.length > 0) {
      setCountries(data.countries);
      if (!selectedCountry) {
        setSelectedCountry(data.countries[0]);
      }
    }
    setAutoPercentage(data.autoApprovalPercentage || 0);
    setTotalApproved(data.totalApproved || 0);
    setAutoApproved(data.autoApproved || 0);
    setManualApproved(data.manualApproved || 0);
    const chartDataArray = [
      { name: 'Auto', value: data.autoApproved || 0 },
      { name: 'Manual', value: data.manualApproved || 0 }
    ];
    setChartData(chartDataArray);
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedFiscalMonth(newMonth);
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return "Select Month";
    // Keep the format as received from backend (e.g., "Jan-2026")
    // Backend should send calendar month name, not fiscal month number
    return monthStr;
  };

  const renderCustomLabel = ({ cx, cy }) => {
    return (
      <text 
        x={cx} 
        y={cy} 
        fill="#000" 
        textAnchor="middle" 
        dominantBaseline="middle"
        style={{ fontSize: '24px', fontWeight: 'bold' }}
      >
        <tspan x={cx} dy="-0.5em">{autoPercentage}%</tspan>
        <tspan x={cx} dy="1.5em" style={{ fontSize: '14px', fontWeight: 'normal' }}>Auto-approved</tspan>
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '8px',
          fontSize: '12px'
        }}>
          <p style={{ margin: 0 }}>{`${payload[0].name}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        {isAdmin ? (
          <>
            <h3 className={styles.title}>Auto-Approval Rate</h3>
            <div className={styles.filterSection}>
              <select 
                className={styles.dropdown}
                value={selectedYear}
                onChange={handleYearChange}
                disabled={availableYearsLoading}
              >
                {(!availableYears || availableYears.length === 0) && <option value="">Select Year</option>}
                {availableYears && availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select 
                className={styles.dropdown}
                value={selectedFiscalMonth}
                onChange={handleMonthChange}
                disabled={!selectedYear || !availableMonthsForYear || availableMonthsForYear.length === 0}
              >
                {(!availableMonthsForYear || availableMonthsForYear.length === 0) ? (
                  <option value="">Select Month</option>
                ) : null}
                {availableMonthsForYear && availableMonthsForYear.map((month) => (
                  <option key={month} value={month}>
                    {formatMonthDisplay(month)}
                  </option>
                ))}
              </select>
              <select 
                className={styles.dropdown}
                value={selectedCountry}
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {country === 'ALL' ? 'All Countries' : (CODE_COUNTRY_FULL_NAME[country] || country)}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <>
            <div className={styles.titleSection}>
              <h3 className={styles.title}>Auto-Approval Rate</h3>
            </div>
            <div className={styles.filterSection}>
              <select 
                className={styles.dropdown}
                value={selectedYear}
                onChange={handleYearChange}
              >
                {availableYears && availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <select 
                className={styles.dropdown}
                value={selectedFiscalMonth}
                onChange={handleMonthChange}
              >
                {availableMonthsForYear.map((month) => (
                  <option key={month} value={month}>
                    {formatMonthDisplay(month)}
                  </option>
                ))}
              </select>
              <select 
                className={styles.dropdown}
                value={selectedCountry}
                onChange={handleCountryChange}
              >
                {countries.map((country) => (
                  <option key={country} value={country}>
                    {CODE_COUNTRY_FULL_NAME[country] || country}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      <div className={styles.chartContainer}>
        {autoApprovalRateLoading ? (
          <div className={styles.noDataContainer}>
            <p>Loading...</p>
          </div>
        ) : (!availableYears || availableYears.length === 0) ? (
          <div className={styles.noDataContainer}>
            <p>No data available.</p>
          </div>
        ) : chartData.length === 0 || totalApproved === 0 ? (
          <div className={styles.noDataContainer}>
            <p>No data available.</p>
          </div>
        ) : (
          <div className={styles.chartWrapper}>
            <ResponsiveContainer width="50%" height={250}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={0}
                  dataKey="value"
                  label={renderCustomLabel}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.name === 'Auto' ? COLORS.auto : COLORS.manual} 
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.statsContainer}>
              <div className={styles.statItem}>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Total Approved</span>
                  <span className={styles.statValue}>{totalApproved}</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statRow}>
                  <span className={styles.dot} style={{ backgroundColor: COLORS.auto }}></span>
                  <span className={styles.statLabel}>Auto</span>
                  <span className={styles.statValue}>{autoApproved}</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statRow}>
                  <span className={styles.dot} style={{ backgroundColor: COLORS.manual }}></span>
                  <span className={styles.statLabel}>Manual</span>
                  <span className={styles.statValue}>{manualApproved}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoApprovalRate;
